<template>
  <div v-if="isFieldVisible(name)" class="form-group">
    <FieldLabel :for="id" :required="required">{{
      label ?? $t(`form.field.${type}.label`)
    }}</FieldLabel>

    <Field
      v-slot="{ field, errorMessage }"
      v-model="organisation"
      :id="id"
      :name="name"
      :rules="rules"
    >
      <div class="input-group has-validation">
        <input
          v-bind="{ ...$attrs, ...field }"
          :class="{ 'is-invalid': errorMessage }"
          :placeholder="placeholder ?? $t(`form.field.school.placeholder`)"
          class="form-control"
          type="text"
        />
        <div class="invalid-feedback">
          {{ errorMessage }}
        </div>
      </div>
    </Field>
  </div>
</template>

<script setup lang="ts">
import { useContactCaptureContext } from "@/components/forms/useContactCapture";
import emojiRegex from "emoji-regex";
import { Field } from "vee-validate";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { string, StringSchema } from "yup";
import FieldLabel from "./FieldLabel.vue";

const { t } = useI18n();

const emojiPattern = emojiRegex();

const props = withDefaults(
  defineProps<{
    id?: string;
    name?: string;
    label?: string;
    placeholder?: string;
    minLength?: number;
    maxLength?: number;
    required?: boolean;
  }>(),
  {
    name: "organisation",
  }
);

const { organisation, role, isFieldVisible } = useContactCaptureContext();

const type = computed(() =>
  role.value === "School" ? "school" : "organisation"
);

const rules = computed<StringSchema<string | undefined>>(() => {
  let schema = string();

  schema = schema.test(
    "no-emoji",
    t("form.field.text.invalid.emoji"),
    (value) => !value || !emojiPattern.test(value)
  );

  if (props.required) {
    schema = schema.required(t("form.field.text.invalid.required"));
  }
  if (props.minLength) {
    schema = schema.min(
      Number(props.minLength),
      t("form.field.text.invalid.required", { n: props.minLength })
    );
  }
  if (props.maxLength) {
    schema = schema.max(
      Number(props.maxLength),
      t("form.field.text.invalid.required", { n: props.maxLength })
    );
  }

  return schema;
});
</script>
